import React from "react";
import { ChatWindow } from "../types";
import PreviewDeviceFrame from "./PreviewDeviceFrame";

interface Props {
  chatWindow: ChatWindow;
}

type ActionType = "url" | "webhook" | "workflow";

const ACTION_TYPE_BADGE: Record<ActionType, { label: string; color: string }> = {
  url: { label: "Opens URL", color: "bg-sky-100 text-sky-700" },
  webhook: { label: "Fires Webhook", color: "bg-violet-100 text-violet-700" },
  workflow: { label: "Triggers Workflow", color: "bg-amber-100 text-amber-700" }
};

export default function CustomActionPreview({ chatWindow }: Props) {
  const lc = chatWindow.languageConfig || {};
  const sc = chatWindow.styleConfig || {};

  const actionColor = sc.actionColor || "#3B82F6";
  const actionType: ActionType = lc.customActionType ?? "url";
  const badge = ACTION_TYPE_BADGE[actionType];

  const title = lc.customActionTitle || "Custom Action";
  const body = lc.customActionBody || "A short description of what this action does.";
  const buttonText = lc.customActionButtonText || "Get Started";
  const enabled = lc.customActionEnabled ?? false;
  const successMsg = lc.customActionSuccessMessage || "Done! Your request was submitted.";
  const errorMsg = lc.customActionErrorMessage || "Something went wrong. Please try again.";

  const launcherPosition: "Left" | "Right" =
    sc.launcherPosition === "Left" ? "Left" : "Right";
  const actualWidth = sc.windowWidth || 370;
  const actualHeight = sc.windowHeight && sc.windowHeight > 0 ? sc.windowHeight : 560;

  const detail = (() => {
    if (actionType === "url")
      return lc.customActionTargetUrl
        ? `→ ${lc.customActionTargetUrl.slice(0, 40)}${lc.customActionTargetUrl.length > 40 ? "…" : ""}`
        : "No URL configured";
    if (actionType === "webhook")
      return lc.customActionWebhookUrl
        ? `${lc.customActionWebhookMethod ?? "POST"} ${lc.customActionWebhookUrl.slice(0, 36)}…`
        : "No endpoint configured";
    if (actionType === "workflow")
      return lc.customActionWorkflowId
        ? `Workflow #${lc.customActionWorkflowId}`
        : "No workflow ID set";
    return "";
  })();

  return (
    <div className="w-full">
      <PreviewDeviceFrame
        launcherPosition={launcherPosition}
        actualWidth={actualWidth}
        actualHeight={actualHeight}
      >
        <div
          className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
          style={{ width: actualWidth, height: actualHeight }}
        >
          {/* Chat header */}
          <div
            className="w-full px-4 py-3 flex items-center space-x-2"
            style={{ backgroundColor: actionColor }}
          >
            <div className="w-7 h-7 rounded-full bg-white/30 flex-shrink-0" />
            <div className="flex flex-col">
              <div className="text-white text-xs font-semibold leading-tight">Support</div>
              <div className="text-white/70 text-[10px]">Online</div>
            </div>
          </div>

          {/* Landing */}
          <div className="bg-white h-full px-3 py-3 flex flex-col space-y-2 overflow-hidden">
            <div className="rounded-xl border border-slate-200 shadow-sm bg-white overflow-hidden">
              <div className="px-3 pt-3 pb-2">
                <div className="flex items-start justify-between mb-1">
                  <div className="text-xs font-semibold text-slate-800 leading-snug flex-1 pr-2 truncate">
                    {title}
                  </div>
                  <span
                    className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${badge.color}`}
                  >
                    {badge.label}
                  </span>
                </div>
                <div className="text-[10px] text-slate-500 leading-snug line-clamp-2 mb-2">
                  {body}
                </div>
                <button
                  className="w-full text-white text-[10px] font-semibold py-1.5 rounded-lg"
                  style={{ backgroundColor: actionColor }}
                >
                  {buttonText}
                </button>
              </div>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1.5">
              <div className="text-[9px] font-semibold text-emerald-600 mb-0.5">On success</div>
              <div className="text-[10px] text-emerald-800 leading-snug">{successMsg}</div>
            </div>
            <div className="rounded-lg border border-red-200 bg-red-50 px-2 py-1.5">
              <div className="text-[9px] font-semibold text-red-500 mb-0.5">On error</div>
              <div className="text-[10px] text-red-700 leading-snug">{errorMsg}</div>
            </div>

            <div className="rounded-xl border border-slate-200 px-3 py-2 bg-white">
              <div className="h-2 bg-slate-100 rounded w-3/4 mb-1" />
              <div className="h-2 bg-slate-100 rounded w-1/2" />
            </div>
          </div>
        </div>
      </PreviewDeviceFrame>

      {/* Config summary below frame */}
      <div className="mt-2 mx-auto w-full max-w-[360px] space-y-1">
        {detail && (
          <div className="text-[11px] text-slate-500 bg-slate-100 rounded px-2 py-1 font-mono truncate">
            {detail}
          </div>
        )}
        {lc.customActionWebhookHasToken && actionType === "webhook" && (
          <div className="text-[11px] text-emerald-600 font-medium">
            ✓ Auth token configured
          </div>
        )}
        {lc.customActionOpenChatAfter && (
          <div className="text-[11px] text-slate-500">
            Opens chat after action fires
          </div>
        )}
        {!enabled && (
          <div className="text-xs text-amber-600 font-medium">
            Widget is disabled — toggle Enable to show it in the chat window.
          </div>
        )}
      </div>
    </div>
  );
}
